// API Base URL - automatically detects environment

// const API_BASE_URL='http://localhost:8000/api';
const API_BASE_URL='https://bookingapi.nazaistudio.ma/api';

// Helper function to handle logout
const handleLogout = () => {
  // Clear all stored tokens and user data
  localStorage.removeItem('adminTokenNazai');
  localStorage.removeItem('adminRefreshTokenNazai');
  localStorage.removeItem('adminUserNazai');

  // Redirect to login page
  window.location.href = '/login';
};

// Helper function to check if error is authentication related
const isAuthError = (error, response) => {
  // Check for 401 status
  if (response.status === 401) {
    return true;
  }

  // Check for token-related error messages
  const tokenErrorMessages = [
    'Given token not valid for any token type',
    'Token is invalid or expired',
    'Authentication credentials were not provided',
    'Invalid token',
    'Token has expired',
    'Signature has expired'
  ];

  const errorText = error.detail || error.error || error.message || '';
  return tokenErrorMessages.some(msg => errorText.includes(msg));
};

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Erreur de connexion' }));

    // Check for authentication errors and try token refresh
    if (isAuthError(error, response)) {
      console.log('Authentication error detected, attempting token refresh...');

      try {
        // Try to refresh the token
        await refreshToken();
        console.log('Token refreshed successfully, retrying request...');

        // Retry the original request with new token
        // Note: This is a simplified approach. In a production app, you might want
        // to implement a more sophisticated retry mechanism
        throw new Error('Token refreshed. Please retry your request.');

      } catch (refreshError) {
        console.log('Token refresh failed, logging out...');

        // Dispatch custom event for auth error
        window.dispatchEvent(new CustomEvent('auth_error', {
          detail: { type: 'auth_error', error: error }
        }));

        handleLogout();
        throw new Error('Session expirée. Redirection vers la page de connexion...');
      }
    }

    // Special handling for validation responses
    if (error.valid === false && error.conflicts) {
      // This is a validation error response - throw the full error object
      throw error;
    }

    // For order validation errors, preserve the original error structure
    if (error.error && error.error.includes('service actif')) {
      throw error; // Throw the full error object to preserve the error message
    }

    // Extract the most relevant error message for other errors
    let errorMessage = 'Une erreur est survenue';

    if (error.error) {
      // Single error message from backend
      errorMessage = error.error;
    } else if (error.non_field_errors && error.non_field_errors.length > 0) {
      // Non-field errors (validation errors)
      errorMessage = error.non_field_errors[0];
    } else if (error.detail) {
      // Detail error message
      errorMessage = error.detail;
    } else if (error.message) {
      // Generic message
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    } else {
      // Fallback based on status code
      switch (response.status) {
        case 400:
          errorMessage = 'Données invalides. Veuillez vérifier vos informations.';
          break;
        case 401:
          errorMessage = 'Session expirée. Veuillez vous reconnecter.';
          break;
        case 403:
          errorMessage = 'Accès non autorisé. Vous n\'avez pas les permissions nécessaires.';
          break;
        case 404:
          errorMessage = 'Ressource introuvable.';
          break;
        case 500:
          errorMessage = 'Erreur serveur. Veuillez réessayer plus tard.';
          break;
        default:
          errorMessage = `Erreur ${response.status}. Veuillez réessayer.`;
      }
    }

    throw new Error(errorMessage);
  }

  // Handle 204 No Content (for DELETE operations)
  if (response.status === 204) {
    return null;
  }

  return response.json();
};

// Helper function to refresh token
const refreshToken = async () => {
  const refreshToken = localStorage.getItem('adminRefreshTokenNazai');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/token/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: refreshToken }),
    });

    if (response.ok) {
      const data = await response.json();
      localStorage.setItem('adminTokenNazai', data.access);
      return data.access;
    } else {
      throw new Error('Token refresh failed');
    }
  } catch (error) {
    console.error('Token refresh error:', error);
    throw error;
  }
};

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('adminTokenNazai');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
};

const adminAPI = {
  // Authentication
  login: async (credentials) => {
    const response = await fetch(`${API_BASE_URL}/auth/login/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials),
    });
    return handleResponse(response);
  },

  logout: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/logout/`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      await handleResponse(response);
    } catch (error) {
      // Even if logout fails on server, clear local data
      console.log('Logout error:', error);
    } finally {
      // Always clear local storage and redirect
      handleLogout();
    }
  },

  // Password Reset
  sendPasswordResetCode: async (email) => {
    const response = await fetch(`${API_BASE_URL}/auth/send-password-reset-code/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    return handleResponse(response);
  },

  verifyPasswordResetCode: async (email, code) => {
    const response = await fetch(`${API_BASE_URL}/auth/verify-password-reset-code/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    });
    return handleResponse(response);
  },

  resetPassword: async (email, code, newPassword) => {
    const response = await fetch(`${API_BASE_URL}/auth/reset-password/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code, new_password: newPassword }),
    });
    return handleResponse(response);
  },

  // Users Management
  getUsers: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.role) params.append('role', filters.role);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page);
    if (filters.dropdown) params.append('dropdown', filters.dropdown);

    const url = `${API_BASE_URL}/admin/users/${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return handleResponse(response);
  },

  createUser: async (userData) => {
    const response = await fetch(`${API_BASE_URL}/admin/users/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData),
    });
    return handleResponse(response);
  },

  updateUser: async (userId, userData) => {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData),
    });
    return handleResponse(response);
  },

  deleteUser: async (userId) => {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Coaches Management
  getCoaches: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/coaches/`, { headers: getAuthHeaders() });
    return handleResponse(response);
  },

  createCoach: async (coachData) => {
    const response = await fetch(`${API_BASE_URL}/admin/coaches/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(coachData),
    });
    return handleResponse(response);
  },

  updateCoach: async (coachId, coachData) => {
    const response = await fetch(`${API_BASE_URL}/admin/coaches/${coachId}/`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(coachData),
    });
    return handleResponse(response);
  },

  deleteCoach: async (coachId) => {
    const response = await fetch(`${API_BASE_URL}/admin/coaches/${coachId}/`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Client Services Management (Legacy - for specific client)
  getClientServicesLegacy: async (clientId) => {
    const response = await fetch(`${API_BASE_URL}/admin/client-services/?client=${clientId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  updateClientService: async (serviceId, serviceData) => {
    const response = await fetch(`${API_BASE_URL}/admin/client-services/${serviceId}/`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(serviceData),
    });
    return handleResponse(response);
  },

  getServiceClients: async (serviceTypeId) => {
    const response = await fetch(`${API_BASE_URL}/admin/client-services/?service_type=${serviceTypeId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getClientBookings: async (clientId) => {
    const response = await fetch(`${API_BASE_URL}/admin/bookings/?client=${clientId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Locations Management
  getLocations: async () => {
    const response = await fetch(`${API_BASE_URL}/locations/`, { headers: getAuthHeaders() });
    return handleResponse(response);
  },

  // Sessions Management
  getSessions: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.date) params.append('date', filters.date);
    if (filters.coach_id) params.append('coach_id', filters.coach_id);
    if (filters.location_id) params.append('location_id', filters.location_id);
    if (filters.week_start) params.append('week_start', filters.week_start);

    // No page_size needed - backend has pagination disabled for sessions
    // This returns ALL sessions without pagination limits

    const url = `${API_BASE_URL}/admin/sessions/${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return handleResponse(response);
  },

  createSession: async (sessionData) => {
    const response = await fetch(`${API_BASE_URL}/admin/sessions/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(sessionData),
    });
    return handleResponse(response);
  },

  updateSession: async (sessionId, sessionData) => {
    const response = await fetch(`${API_BASE_URL}/admin/sessions/${sessionId}/`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(sessionData),
    });
    return handleResponse(response);
  },

  // Get session group (all sessions created together)
  getSessionGroup: async (sessionId) => {
    const response = await fetch(`${API_BASE_URL}/admin/sessions/${sessionId}/group/`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  deleteSession: async (sessionId) => {
    const response = await fetch(`${API_BASE_URL}/admin/sessions/${sessionId}/`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Validate multiple sessions before creation
  validateSessionsBulk: async (sessionsData) => {
    const response = await fetch(`${API_BASE_URL}/admin/sessions/validate-bulk/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ sessions: sessionsData }),
    });
    return handleResponse(response);
  },

  // Get session bookings
  getSessionBookings: async (sessionId) => {
    const response = await fetch(`${API_BASE_URL}/admin/sessions/${sessionId}/bookings/`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Get session waitlist
  getSessionWaitlist: async (sessionId) => {
    const response = await fetch(`${API_BASE_URL}/admin/sessions/${sessionId}/waitlist/`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  createLocation: async (locationData) => {
    const response = await fetch(`${API_BASE_URL}/locations/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(locationData),
    });
    return handleResponse(response);
  },

  updateLocation: async (locationId, locationData) => {
    const response = await fetch(`${API_BASE_URL}/locations/${locationId}/`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(locationData),
    });
    return handleResponse(response);
  },

  deleteLocation: async (locationId) => {
    const response = await fetch(`${API_BASE_URL}/locations/${locationId}/`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },



  // Bookings Management
  getBookings: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.user_id) params.append('user_id', filters.user_id);
    if (filters.session_id) params.append('session_id', filters.session_id);
    if (filters.coach_id) params.append('coach_id', filters.coach_id);
    if (filters.location_id) params.append('location_id', filters.location_id);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.date) params.append('date', filters.date);
    if (filters.client_id) params.append('client_id', filters.client_id);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page);

    const url = `${API_BASE_URL}/admin/bookings/${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return handleResponse(response);
  },

  createBooking: async (bookingData) => {
    const response = await fetch(`${API_BASE_URL}/admin/bookings/create/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(bookingData),
    });
    return handleResponse(response);
  },

  updateBooking: async (bookingId, bookingData) => {
    const response = await fetch(`${API_BASE_URL}/admin/bookings/${bookingId}/`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(bookingData),
    });
    return handleResponse(response);
  },

  deleteBooking: async (bookingId) => {
    const response = await fetch(`${API_BASE_URL}/admin/bookings/${bookingId}/`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Waitlist Management
  confirmWaitlistEntry: async (waitlistId) => {
    const response = await fetch(`${API_BASE_URL}/admin/waitlist/${waitlistId}/confirm/`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  deleteWaitlistEntry: async (waitlistId) => {
    const response = await fetch(`${API_BASE_URL}/admin/waitlist/${waitlistId}/delete/`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Orders Management
  getOrders: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.payment_method) params.append('payment_method', filters.payment_method);
    if (filters.client_id) params.append('client_id', filters.client_id);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page);

    const url = `${API_BASE_URL}/admin/orders/${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return handleResponse(response);
  },

  createOrder: async (orderData) => {
    const response = await fetch(`${API_BASE_URL}/admin/orders/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(orderData),
    });
    return handleResponse(response);
  },

  updateOrder: async (orderId, orderData) => {
    const response = await fetch(`${API_BASE_URL}/admin/orders/${orderId}/`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(orderData),
    });
    return handleResponse(response);
  },

  deleteOrder: async (orderId) => {
    const response = await fetch(`${API_BASE_URL}/admin/orders/${orderId}/`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Client Services Management
  getClientServices: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.client_id) params.append('client_id', filters.client_id);
    if (filters.status) params.append('status', filters.status);
    if (filters.service_type) params.append('service_type', filters.service_type);
    if (filters.page) params.append('page', filters.page);

    const url = `${API_BASE_URL}/admin/client-services/${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return handleResponse(response);
  },

  // Service Types Management (Admin)
  // Supports pagination: pass { page, page_size, search, is_active }. Returns { count, next, previous, results } when paginated.
  getServiceTypes: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page);
    if (filters.page_size) params.append('page_size', filters.page_size);
    if (filters.search) params.append('search', filters.search);
    if (filters.is_active === true || filters.is_active === 'true') params.append('is_active', 'true');
    if (filters.is_active === false || filters.is_active === 'false') params.append('is_active', 'false');

    const url = `${API_BASE_URL}/admin/service-types/${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return handleResponse(response);
  },

  // Get ALL active service types (no pagination) - for dropdowns
  getAllActiveServiceTypes: async () => {
    let all = [];
    let page = 1;
    let hasMore = true;
    while (hasMore) {
      const params = new URLSearchParams({ page, page_size: 100, is_active: 'true' });
      const url = `${API_BASE_URL}/admin/service-types/?${params.toString()}`;
      const response = await fetch(url, { headers: getAuthHeaders() });
      const data = await handleResponse(response);
      const list = data?.results ?? data;
      if (Array.isArray(list)) all.push(...list);
      hasMore = !!data?.next;
      page++;
    }
    return all;
  },

  getServiceType: async (serviceId) => {
    const response = await fetch(`${API_BASE_URL}/admin/service-types/${serviceId}/`, { headers: getAuthHeaders() });
    return handleResponse(response);
  },

  createServiceType: async (serviceData) => {
    const response = await fetch(`${API_BASE_URL}/admin/service-types/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(serviceData),
    });
    return handleResponse(response);
  },

  updateServiceType: async (serviceId, serviceData) => {
    const response = await fetch(`${API_BASE_URL}/admin/service-types/${serviceId}/`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(serviceData),
    });
    return handleResponse(response);
  },

  deleteServiceType: async (serviceId) => {
    const response = await fetch(`${API_BASE_URL}/admin/service-types/${serviceId}/`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Services Management (Client)
  getServices: async () => {
    const response = await fetch(`${API_BASE_URL}/services/`, { headers: getAuthHeaders() });
    return handleResponse(response);
  },

  createService: async (serviceData) => {
    const response = await fetch(`${API_BASE_URL}/services/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(serviceData),
    });
    return handleResponse(response);
  },

  updateService: async (serviceId, serviceData) => {
    const response = await fetch(`${API_BASE_URL}/services/${serviceId}/`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(serviceData),
    });
    return handleResponse(response);
  },

  deleteService: async (serviceId) => {
    const response = await fetch(`${API_BASE_URL}/services/${serviceId}/`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Analytics
  getAnalytics: async (params = {}) => {
    const queryParams = new URLSearchParams();

    // Add parameters if provided
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.period) queryParams.append('period', params.period);

    const queryString = queryParams.toString();
    const url = queryString ? `${API_BASE_URL}/analytics/?${queryString}` : `${API_BASE_URL}/analytics/`;

    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Get all notifications (admin only)
  getNotifications: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        params.append(key, value);
      }
    });

    const response = await fetch(`${API_BASE_URL}/admin/notifications/?${params}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Get notification count (admin only)
  getNotificationCount: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/notifications/count/`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
};

export default adminAPI;
